<template>
  <div id="supplier-trading-agreements" class="fill-height align-start">
    <i-overlay v-if="loading && $vuetifyDisplay.smAndDown" text="Loading..." />

    <v-row :class="$route.meta.class" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0 text-center">{{ $route.meta.title }}</h2>
      </v-col>

      <i-progress-linear v-if="loading && !$vuetifyDisplay.smAndDown" />

      <v-row no-gutters class="mt-4 mt-md-6" v-if="!loading">
        <v-col
          v-if="!userAgreementOnly && featureCreditReturnsEnabled"
          cols="12"
        >
          <v-card class="p-main-card">
            <v-simple-table>
              <thead>
                <tr class="p-row-header">
                  <th colspan="2">
                    <h3 class="g-heading-s text-center my-3">Fees</h3>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="p-col-left">Optional - Credit Returns</td>
                  <td class="p-col-right">
                    {{
                      $filters.percentage(
                        supplierFees.creditReturnFeePercentage
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col v-if="!userAgreementOnly" cols="12">
          <v-card class="p-main-card mt-8" v-if="ownerControlledPaymentDays">
            <v-simple-table>
              <thead>
                <tr class="p-row-header">
                  <th colspan="2">
                    <h3 class="g-heading-s text-center my-3">
                      Central Agreements
                    </h3>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="p-col-left">Payout Days</td>
                  <td class="p-col-right">{{ supplierPayoutDays }}</td>
                </tr>
                <tr :key="rule.id" v-for="rule in tradingTermsRules">
                  <td class="p-col-left">
                    Trading Terms ({{ targetGeography(rule) }})
                  </td>
                  <td class="p-col-right">
                    {{ $filters.percentage(rule.supplierChargePercentage) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col v-if="tenantAgreementEnabled && hasTenantAgreement" cols="12">
          <v-card class="p-main-card mt-8">
            <v-simple-table>
              <thead>
                <tr class="p-row-header">
                  <th colspan="2">
                    <h3 class="g-heading-s text-center my-3">User Agreement</h3>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="document in tenantAgreementDocuments"
                  :key="document.id"
                >
                  <td class="p-col-left">
                    {{ document.fileName || 'agreement.pdf' }}
                  </td>
                  <td class="p-col-right">
                    <v-btn icon @click="viewTenantAgreement(document)">
                      <v-icon>mdi-eye-outline</v-icon>
                    </v-btn>
                    <v-btn icon @click="downloadTenantAgreement(document)">
                      <v-icon>mdi-download-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  fetchSupplierPaymentDays,
  fetchSupplierTradingTermsRules
} from '@/services/tenantOwner'
import {
  downloadTenantAgreementDocument,
  fetchTenantAgreement
} from '@/services/tenantAgreement'
import iOverlay from '@/components/Overlay.vue'
import iProgressLinear from '@/components/ProgressLinear.vue'

export default {
  components: { iOverlay, iProgressLinear },
  computed: {
    ...mapGetters('tenant', [
      'ownerControlledPaymentDays',
      'featureCreditReturnsEnabled',
      'tenantAgreementEnabled'
    ]),
    ...mapState('profile', {
      supplierFees: (state) => state.info?.fees,
      supplierId: (state) => state.info?.code || state.info?.id
    }),

    supplierPayoutDays() {
      return this.paymentDays?.supplierPayoutDays
    },

    tenantAgreementDocuments() {
      if (this.tenantAgreement?.documents?.length) {
        return this.tenantAgreement.documents
      }
      if (this.tenantAgreement?.hasDocument) {
        return [
          {
            id: null,
            fileName: this.tenantAgreement.fileName,
            uploadedAt: this.tenantAgreement.uploadedAt
          }
        ]
      }
      return []
    },

    hasTenantAgreement() {
      return this.tenantAgreementDocuments.length > 0
    },

    userAgreementOnly() {
      return this.$route.name === 'supplier-user-agreement'
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    targetGeography(rule) {
      const targetStoreStates = rule.targetStoreStates?.length > 0
      if (targetStoreStates) {
        return rule.targetStoreStates
          .map((it) => it.state)
          .sort()
          .join(', ')
      } else {
        return 'NATIONAL'
      }
    },

    async load() {
      this.loading = true
      try {
        const tenantAgreementPromise =
          this.tenantAgreementEnabled && this.supplierId
            ? fetchTenantAgreement(this.supplierId)
            : Promise.resolve(null)

        if (!this.userAgreementOnly) {
          this.paymentDays = await fetchSupplierPaymentDays()
          this.tradingTermsRules = await fetchSupplierTradingTermsRules()
        }

        this.tenantAgreement = await tenantAgreementPromise
      } catch {
        this.addNotification({
          color: 'error',
          text: 'Trading Agreements could not be loaded, please try again...'
        })
      } finally {
        this.loading = false
      }
    },

    async viewTenantAgreement(agreementDocument) {
      const blob = await downloadTenantAgreementDocument(
        this.supplierId,
        agreementDocument.id,
        true
      )
      window.open(URL.createObjectURL(blob), '_blank')
    },

    async downloadTenantAgreement(agreementDocument) {
      const blob = await downloadTenantAgreementDocument(
        this.supplierId,
        agreementDocument.id,
        false
      )
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = agreementDocument.fileName || 'agreement.pdf'
      link.click()
    }
  },

  data: () => ({
    loading: false,
    paymentDays: null,
    tradingTermsRules: [],
    tenantAgreement: null
  }),

  async mounted() {
    await this.load()
  }
}
</script>

<style lang="scss" scoped>
#supplier-trading-agreements {
  .p-main-card {
    padding: 8px;
    max-width: 740px;
    margin: auto;

    .p-col-left {
      width: 60%;
    }

    .p-col-right {
      margin-top: 5px;
      text-align: center;
    }

    .p-row-header {
      * {
        background-color: $primary-color;
        color: white;
        margin-bottom: 0px;
        padding-bottom: 0px;
        border-radius: 5px 5px 0px 0px;
      }
    }

    .p-row-total {
      * {
        font-weight: bold;
        font-size: 18px !important;
        // border-radius: 0px 0px 5px 5px;
        border-top: 3px solid $primary-color;
        border-bottom: 3px solid $primary-color;
      }

      .p-col-left {
        background-color: var(--v-lightHighlightBackground-lighten1);
      }

      .p-col-right {
        background-color: $green-color-bg;
      }
    }
  }
}

@media only screen and (min-width: 960px) {
  #supplier-trading-agreements {
    .p-main-card {
      padding: 16px;

      .p-col-left {
        width: 75%;
      }
    }
  }
}
</style>
